import { ColumnDef } from '@tanstack/react-table';
import { SeatingCell } from 'src/components/TableV2/cells/SeatingCell';
import {
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { SEATING_SORTING_FN_NAME } from 'src/tables/common/hooks/useSeatingColumnSorting';
import { TableShimmeringDiv } from 'src/tables/Table';
import { SectionType } from 'src/utils/types/sectionType';
import {
  ListingNote,
  PointOfSaleSaleStatus,
  Seating,
} from 'src/WebApiController';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';
import { seatingSectionFilter } from '../filters/filterFunctions';

interface SeatingColumnOptions<TData> {
  /** Column ID */
  id: string;
  /** Content ID for the header label */
  headerContentId: string;
  /** Accessor function to extract seating data */
  accessor: (data: TData) => {
    seating: Seating;
    listingNotes: ListingNote[] | null;
    isSeatSaver: boolean;
    status?: PointOfSaleSaleStatus;
    isMultiple?: boolean;
  } | null;
  /** Accessor for marketplace problem flag */
  hasMarketplaceProblemAccessor?: (data: TData) => boolean;
  /** Column size */
  size?: number;
  /** Section type */
  sectionType?: SectionType;
  /** Enable column filter */
  enableColumnFilter?: boolean;
  /** Custom filter function */
  filterFn?: any;
  /** Custom sorting function */
  sortingFn?: any;
  /** Meta for filter variant */
  meta?: Record<string, any>;
}

/**
 * Factory function to create seating column definitions for tables.
 * Handles seating display with user-defined precision and seat traits.
 *
 * @example
 * createSeatingColumn({
 *   id: SalesTableColumnId.Seating,
 *   headerContentId: SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Seating],
 *   accessor: (data) => ({
 *     seating: data?.sale?.seating,
 *     listingNotes: data?.sale?.seatTraits,
 *     isSeatSaver: data?.sale?.isSeatSaver,
 *     status: data?.sale?.status,
 *   }),
 *   hasMarketplaceProblemAccessor: (data) => data?.sale?.hasMarketplaceProblem ?? false,
 *   sortingFn: SEATING_SORTING_FN_NAME,
 * })
 */
export const createSeatingColumn = <TData,>(
  options: SeatingColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    id,
    headerContentId,
    accessor,
    hasMarketplaceProblemAccessor = () => false,
    size,
    sectionType = SectionType.Sales,
    enableColumnFilter = true,
    filterFn,
    sortingFn = SEATING_SORTING_FN_NAME,
    meta = {
      uniqueValueVariant: 'seatingSection',
    },
  } = options;

  // Smart default: use seatingSectionFilter if filtering is enabled and no custom filterFn
  const actualFilterFn =
    filterFn ?? (enableColumnFilter ? seatingSectionFilter : undefined);

  return {
    id,
    enableColumnFilter,
    ...(actualFilterFn && { filterFn: actualFilterFn }),
    ...(sortingFn && { sortingFn }),
    meta: {
      ...meta,
      ...(enableColumnFilter && { columnFilterVariant: 'multiSelect' }),
    },
    size,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: () => <Content id={headerContentId} />,
    accessorFn: accessor,
    cell: function Cell({ getValue, row: { original }, column, table }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        column.id,
        table.options.meta?.sectionType || sectionType
      );

      const value = getValue<{
        seating: Seating;
        listingNotes: ListingNote[] | null;
        isSeatSaver: boolean;
        status?: PointOfSaleSaleStatus;
        isMultiple?: boolean;
      } | null>();

      if (value == null || !original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const { seating, listingNotes, isSeatSaver, status, isMultiple } = value;
      const hasMarketplaceProblem = hasMarketplaceProblemAccessor(original);

      return (
        <SeatingCell
          section={seating.section}
          row={seating.row}
          seatFr={seating.seatFr}
          seatTo={seating.seatTo}
          isSeatSaver={isSeatSaver}
          isWasted={status === PointOfSaleSaleStatus.Wasted}
          hasMarketplaceProblem={hasMarketplaceProblem}
          userDefinedPrecision={userDefinedPrecision}
          seatTraits={listingNotes}
          isMultiple={isMultiple}
        />
      );
    },
  };
};
